import React from 'react';
import { userService } from 'common/services/user.service';
import ProductDetailsPage from 'modules/common/product-details-page';
import { Route } from 'common/types/tech';
import { RetailerType } from '../entities';
import { RoutesAvailability } from './routes-availability';

const TemplateEditor = React.lazy(() => import('modules/walmart/templates-module/components/pages/template-editor'));
const TemplatesList = React.lazy(() => import('modules/walmart/templates-module/components/pages/templates-list'));
const ContentList = React.lazy(() => import('modules/common/content/components/pages/content-list'));
const ContentReview = React.lazy(() => import('modules/common/content/components/pages/content-review'));
const QuickReview = React.lazy(() => import('modules/common/content/components/pages/quick-review'));
const QuickStart = React.lazy(() => import('modules/common/quick-start'));
const Settings = React.lazy(() => import('modules/common/settings'));
const Overview = React.lazy(() => import('modules/walmart/overview'));
const Comments = React.lazy(() => import('modules/walmart/comments'));
const TargetComments = React.lazy(() => import('modules/target/comments'));
const AIQueueV3 = React.lazy(() => import('modules/common/ai/components/pages/ai-queue-v3'));
const AIBenchmark = React.lazy(() => import('modules/common/ai/components/pages/ai-benchmark'));
const TeamsBenchmark = React.lazy(() => import('modules/common/ai/components/pages/teams-benchmark'));
const TeamsBenchmarkStats = React.lazy(() => import('modules/common/ai/components/pages/teams-benchmark-stats'));
const AIBenchmarkStats = React.lazy(() => import('modules/common/ai/components/pages/ai-benchmark-stats'));
const AIInstructions = React.lazy(() => import('modules/common/ai/components/pages/ai-instructions'));
const ImageScoring = React.lazy(() => import('modules/common/ai/components/pages/image-scoring'));
const SwatchCreator = React.lazy(() => import('modules/common/ai/components/pages/swatch-creator'));
const BackgroundCreator = React.lazy(() => import('modules/common/ai/components/pages/background-creator'));
const CategoriesSettings = React.lazy(() => import('modules/common/categories-settings'));
const AISearch = React.lazy(() => import('modules/common/ai-search'));
const AddTcins = React.lazy(() => import('modules/target/products-enrichment/components/pages/add-tcins'));
const RulesHistory = React.lazy(() => import('modules/target/products-enrichment/components/pages/rules-history'));
const WalmartRulesHistory = React.lazy(() => import('modules/walmart/products-enrichment/pages/rules-history'));
const AddSkus = React.lazy(() => import('modules/walmart/products-enrichment/pages/add-wpids'));
const ProductDetailsAI = React.lazy(() => import('modules/common/product-details-ai'));
const Leaderboard = React.lazy(() => import('modules/common/leaderboard'));
const SanitizePreview = React.lazy(() => import('modules/common/sanitize-preview'));
const GSheetBatchUpdate = React.lazy(() => import('modules/common/gsheet-batch-update'));
const KeywordsTool = React.lazy(() => import('modules/common/keywords-tool'));
const WarningRules = React.lazy(() => import('modules/common/warning-rules'));
const OverviewPage = React.lazy(() => import('modules/common/overview-page'));
const AiSettings = React.lazy(() => import('modules/common/ai-settings'));
const Suppliers = React.lazy(() => import('modules/common/suppliers'));
const ProductSKURedirect = React.lazy(() => import('modules/common/ai/components/pages/product-sku-redirect'));
const VideoRedirectPage = React.lazy(() => import('modules/video-app/redirect-page'));

const isL3Walmart = () => userService.ensureRetailer(RetailerType.Walmart) && userService.ensureLevel('L3');

export const getAttributesRoutes = (): Array<Route> => [
  {
    path: '/',
    preview:
      userService.ensureRetailer(RetailerType.Walmart) && !userService.ensureLevel('L3') ? 'Overview' : 'Products',
    component:
      userService.ensureRetailer(RetailerType.Walmart) && !userService.ensureLevel('L3') ? Overview : ContentList,
    checkAvailability: () => RoutesAvailability.Overview(),
    protected: true,
  },
  {
    path: '/content',
    preview: isL3Walmart() ? 'Products' : 'Content Management',
    component: ContentList,
    checkAvailability: () => RoutesAvailability.ContentManagement(),
    protected: true,
  },
  {
    path: '/content/:pid',
    preview: 'Content Review',
    component: ContentReview,
    checkAvailability: () => RoutesAvailability.ContentReview(),
    protected: true,
  },
  {
    path: '/product/:pid',
    preview: 'Content Review',
    component: ContentReview,
    checkAvailability: () => RoutesAvailability.ProductReview(),
    protected: true,
  },
  {
    path: '/pdp/:pid',
    preview: 'Product Details',
    component: ProductDetailsPage,
    checkAvailability: () => RoutesAvailability.PDP(),
    protected: true,
  },
  {
    path: '/content/quick-review',
    preview: 'Quick Review',
    component: QuickReview,
    checkAvailability: () => RoutesAvailability.QuickReview(),
    hiddenLayout: true,
    protected: true,
  },
  {
    path: '/start',
    preview: 'Quick Start',
    component: QuickStart,
    checkAvailability: () => RoutesAvailability.QuickStart(),
    hiddenLayout: !userService.ensureLevel('L3'),
    protected: true,
  },
  {
    path: '/templates',
    preview: 'Templates Management',
    component: TemplatesList,
    checkAvailability: () => RoutesAvailability.TemplatesManagement(),
    protected: true,
  },
  {
    path: '/templates/:id',
    preview: 'Template Edit',
    component: TemplateEditor,
    checkAvailability: () => RoutesAvailability.TemplateEditor(),
    protected: true,
  },
  {
    path: '/support',
    preview: 'Support',
    component: () => <div>Support</div>,
    checkAvailability: () => RoutesAvailability.Support(),
    protected: true,
  },
  {
    path: '/settings/:nav',
    preview: 'Settings',
    component: Settings,
    checkAvailability: () => RoutesAvailability.Settings(),
    protected: true,
  },
  {
    path: '/comments',
    preview: 'Comments',
    component: userService.ensureRetailer(RetailerType.Walmart) ? Comments : TargetComments,
    checkAvailability: () => RoutesAvailability.Comments(),
    protected: true,
  },
  {
    path: '/ai-queue',
    preview: 'AI Queue',
    component: AIQueueV3,
    checkAvailability: () => RoutesAvailability.AIQueue(),
    protected: true,
  },
  {
    path: '/keywords-tool',
    preview: 'Keywords Tool',
    component: KeywordsTool,
    checkAvailability: () => RoutesAvailability.KeywordsTool(),
    protected: true,
  },
  {
    path: '/warning-rules',
    preview: 'Warning Rules',
    component: WarningRules,
    checkAvailability: () => RoutesAvailability.WarningRules(),
    protected: true,
  },
  {
    path: '/ai-instructions',
    preview: 'AI Instructions',
    component: AIInstructions,
    checkAvailability: () => RoutesAvailability.AIInstructions(),
    protected: true,
  },
  {
    path: '/ai-benchmark',
    preview: 'AI Benchmark',
    component: AIBenchmark,
    checkAvailability: () => RoutesAvailability.AIBenchmark(),
    protected: true,
  },
  {
    path: '/compare-benchmark',
    preview: 'Compare Benchmark',
    component: TeamsBenchmark,
    checkAvailability: () => RoutesAvailability.CompareBenchmark(),
    protected: true,
  },
  {
    path: '/compare-benchmark-stats',
    preview: 'Compare Benchmark Stats',
    component: TeamsBenchmarkStats,
    checkAvailability: () => RoutesAvailability.CompareBenchmarkStats(),
    protected: true,
  },
  {
    path: '/ai-benchmark-stats',
    preview: 'AI Benchmark Stats',
    component: AIBenchmarkStats,
    checkAvailability: () => RoutesAvailability.AIBenchmarkStats(),
    protected: true,
  },
  {
    path: '/categories-settings',
    preview: 'Categories Settings',
    component: CategoriesSettings,
    checkAvailability: () => RoutesAvailability.CategoriesSettings(),
    protected: true,
  },
  {
    path: '/add-tcins',
    preview: 'Add Product IDs',
    component: AddTcins,
    checkAvailability: () => RoutesAvailability.AddTCINs(),
    protected: true,
  },
  {
    path: '/add-asins',
    preview: 'Add Product IDs',
    component: AddTcins,
    checkAvailability: () => RoutesAvailability.AddASINs(),
    protected: true,
  },
  {
    path: '/add-xlsx',
    preview: 'Add XLSX',
    component: AddTcins,
    checkAvailability: () => RoutesAvailability.AddXLSX(),
    protected: true,
  },
  {
    path: '/change-tracking',
    preview: userService.ensureRetailer(RetailerType.Walmart) ? 'Walmart Change Tracking' : 'Target Change Tracking',
    component: userService.ensureRetailer(RetailerType.Walmart) ? WalmartRulesHistory : RulesHistory,
    checkAvailability: () => RoutesAvailability.ChangeTracking(),
    protected: true,
  },
  {
    path: '/add-skus',
    preview: 'Add Product IDs',
    component: AddSkus,
    checkAvailability: () => RoutesAvailability.AddSKUs(),
    protected: true,
  },
  {
    path: '/search',
    preview: 'AI Search',
    component: AISearch,
    checkAvailability: () => RoutesAvailability.AISearch(),
    hiddenLayout: true,
    protected: true,
  },
  {
    path: '/image-scoring',
    preview: 'Image Scoring',
    component: ImageScoring,
    checkAvailability: () => RoutesAvailability.ImageScoring(),
    protected: true,
  },
  {
    path: '/swatch-creator',
    preview: 'Swatch Creator',
    component: SwatchCreator,
    checkAvailability: () => RoutesAvailability.SwatchCreator(),
    protected: true,
  },
  {
    path: '/background-creator',
    preview: 'Background Creator',
    component: BackgroundCreator,
    checkAvailability: () => RoutesAvailability.BackgroundCreator(),
    hiddenLayout: true,
    protected: true,
  },
  {
    path: '/leaderboard',
    preview: 'Leaderboard',
    component: Leaderboard,
    checkAvailability: () => RoutesAvailability.Leaderboard(),
    protected: true,
  },
  {
    path: '/sanitize/:op_id',
    preview: 'Sanitize Preview',
    component: SanitizePreview,
    checkAvailability: () => RoutesAvailability.SanitizePreview(),
    protected: true,
  },
  {
    path: '/gsheet-batch',
    preview: 'GSheet Batch Update',
    component: GSheetBatchUpdate,
    checkAvailability: () => RoutesAvailability.GSheetBatchUpdate(),
    protected: true,
  },
  {
    path: '/pdp-gen',
    preview: 'Product Details Generator',
    component: ProductDetailsAI,
    checkAvailability: () => true,
    protected: true,
  },
  {
    path: '/pdp-gen/:pipelineID',
    preview: 'Product Details Generator',
    component: ProductDetailsAI,
    checkAvailability: () => true,
    protected: true,
  },
  {
    path: '/overview',
    preview: 'Overview',
    component: OverviewPage,
    checkAvailability: () => RoutesAvailability.OverviewPage(),
    protected: true,
  },
  {
    path: '/ai-settings',
    preview: 'Retailer AI Settings',
    component: AiSettings,
    checkAvailability: () => RoutesAvailability.AISettings(),
    protected: true,
  },
  {
    path: '/suppliers',
    preview: 'Suppliers',
    component: Suppliers,
    checkAvailability: () => RoutesAvailability.Suppliers(),
    protected: true,
  },
  {
    path: '/product/sku/:sku',
    preview: 'Redirect to Product Page by SKU',
    component: ProductSKURedirect,
    checkAvailability: () => RoutesAvailability.ProductSKU(),
    hiddenLayout: true,
    protected: true,
  },
  {
    path: '/video-redirect',
    preview: 'Video App Redirect',
    component: VideoRedirectPage,
    checkAvailability: () => true,
    hiddenLayout: true,
    protected: true,
  },
];
